// @ts-nocheck
// ============================================================
// OPERATIONAL MAP — Fase 10.12
// Mapa multi-camada: SST, produção, supply, logística, etc.
// ============================================================
import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTwinStore } from '@/store/twinStore'
import { LayerToggle, SpatialRiskOverlay, RiskBadge } from '@/design-system/twin'
import { SURFACE, TEXT } from '@/design-system/tokens'
import type { SpatialRisk } from '@/types/twin'

type LayerId = 'sst' | 'producao' | 'supply' | 'logistica' | 'equipes' | 'clima' | 'equipamentos' | 'visao'

const LAYERS: Array<{ id: LayerId; label: string; icon: string; color: string; riskType?: string }> = [
  { id: 'sst',          label: 'SST',           icon: '🦺', color: '#ef4444', riskType: 'sst' },
  { id: 'producao',     label: 'Produção',       icon: '🏗️', color: '#3b82f6', riskType: 'production' },
  { id: 'supply',       label: 'Supply',         icon: '📦', color: '#f59e0b', riskType: 'supply' },
  { id: 'logistica',    label: 'Logística',      icon: '🚛', color: '#8b5cf6', riskType: 'logistics' },
  { id: 'equipes',      label: 'Equipes',        icon: '👷', color: '#10b981' },
  { id: 'clima',        label: 'Clima',          icon: '🌧️', color: '#64748b', riskType: 'weather' },
  { id: 'equipamentos', label: 'Equipamentos',   icon: '⚙️', color: '#f97316' },
  { id: 'visao',        label: 'Visão',          icon: '📹', color: '#06b6d4' },
]

export function OperationalMap() {
  const { twinState } = useTwinStore()
  const [activeLayers, setActiveLayers] = useState<Set<LayerId>>(new Set(['sst', 'producao', 'supply']))
  const [hoveredZone, setHoveredZone] = useState<string | null>(null)

  const toggleLayer = (id: LayerId) => {
    setActiveLayers(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const visibleRisks = useMemo<SpatialRisk[]>(() => {
    if (!twinState) return []
    const activeRiskTypes = LAYERS
      .filter(l => activeLayers.has(l.id) && l.riskType)
      .map(l => l.riskType!)
    return twinState.risks.filter(r => r.is_active && activeRiskTypes.includes(r.risk_type))
  }, [twinState, activeLayers])

  const zones = twinState?.zones ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      {/* Layer toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {LAYERS.map(layer => (
          <LayerToggle
            key={layer.id}
            id={layer.id}
            label={layer.label}
            icon={layer.icon}
            active={activeLayers.has(layer.id)}
            color={layer.color}
            onToggle={() => toggleLayer(layer.id)}
          />
        ))}
      </div>

      {/* Map canvas */}
      <div style={{
        flex:         1,
        position:     'relative',
        background:   '#0d1117',
        border:       '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        overflow:     'hidden',
        minHeight:    320,
      }}>
        {/* Grid background */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.15 }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#334155" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Zone layout — relative positions */}
        <MapLayout
          zones={zones}
          risks={visibleRisks}
          activeLayers={activeLayers}
          hoveredZone={hoveredZone}
          setHoveredZone={setHoveredZone}
        />

        {/* Equipment layer */}
        {activeLayers.has('equipamentos') && twinState?.equipment && (
          <EquipmentLayer equipment={twinState.equipment} />
        )}

        {/* Legend */}
        <div style={{
          position:     'absolute',
          bottom:       12,
          right:        12,
          background:   'rgba(0,0,0,0.7)',
          border:       '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8,
          padding:      '8px 12px',
          display:      'flex',
          flexDirection:'column',
          gap:          4,
        }}>
          {[
            { color: '#10b981', label: 'Baixo risco' },
            { color: '#f59e0b', label: 'Moderado' },
            { color: '#f97316', label: 'Alto' },
            { color: '#ef4444', label: 'Crítico' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />
              <span style={{ fontSize: 10, color: TEXT.muted }}>{l.label}</span>
            </div>
          ))}
        </div>

        {/* Risk count badge */}
        {visibleRisks.length > 0 && (
          <div style={{
            position:   'absolute',
            top:        12,
            right:      12,
            background: 'rgba(239,68,68,0.15)',
            border:     '1px solid rgba(239,68,68,0.4)',
            borderRadius: 6,
            padding:    '4px 8px',
            fontSize:   11,
            color:      '#ef4444',
            fontWeight: 700,
          }}>
            {visibleRisks.length} riscos visíveis
          </div>
        )}
      </div>

      {/* Risk list for visible layers */}
      {visibleRisks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
          <div style={{ fontSize: 11, color: TEXT.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Riscos nas camadas ativas
          </div>
          {visibleRisks.slice(0, 8).map(risk => (
            <RiskRow key={risk.id} risk={risk} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Map Layout ───────────────────────────────────────────
function MapLayout({
  zones, risks, activeLayers, hoveredZone, setHoveredZone,
}: {
  zones: any[]
  risks: SpatialRisk[]
  activeLayers: Set<LayerId>
  hoveredZone: string | null
  setHoveredZone: (id: string | null) => void
}) {
  if (zones.length === 0) {
    return (
      <div style={{
        position:   'absolute', inset: 0,
        display:    'flex', alignItems: 'center', justifyContent: 'center',
        color:      TEXT.muted, fontSize: 13,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
          <div>Nenhuma zona espacial configurada</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Configure as zonas no Digital Twin para visualizar o mapa</div>
        </div>
      </div>
    )
  }

  // Auto-layout: simple grid if no spatial_bounds
  return (
    <div style={{
      position: 'absolute', inset: 12,
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(zones.length))}, 1fr)`,
      gap: 6,
    }}>
      {zones.map(zone => {
        const zoneRisks = risks.filter(r => r.zone_id === zone.id)
        const isHovered = hoveredZone === zone.id
        const RISK_COLOR: Record<string, string> = {
          low: '#10b981', moderate: '#f59e0b', high: '#f97316',
          critical: '#ef4444', emergency: '#dc2626',
        }
        const color = RISK_COLOR[zone.risk_level] ?? '#10b981'

        return (
          <motion.div
            key={zone.id}
            whileHover={{ scale: 1.03 }}
            onHoverStart={() => setHoveredZone(zone.id)}
            onHoverEnd={() => setHoveredZone(null)}
            style={{
              background:   `${color}18`,
              border:       `1.5px solid ${color}${isHovered ? 'cc' : '44'}`,
              borderRadius: 6,
              padding:      '8px 6px',
              cursor:       'default',
              position:     'relative',
              minHeight:    60,
              display:      'flex',
              flexDirection:'column',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color, lineHeight: 1.2, wordBreak: 'break-word' }}>
              {zone.nome}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <span style={{ fontSize: 9, color: '#64748b' }}>{zone.tipo}</span>
              {zoneRisks.length > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 700,
                  background: `${color}33`, color,
                  borderRadius: 3, padding: '1px 3px',
                }}>
                  {zoneRisks.length}⚠
                </span>
              )}
            </div>
            {(zone.risk_level === 'critical' || zone.risk_level === 'emergency') && (
              <motion.div
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                style={{
                  position: 'absolute', inset: 0,
                  border: `2px solid ${color}`,
                  borderRadius: 6, pointerEvents: 'none',
                }}
              />
            )}
          </motion.div>
        )
      })}
    </div>
  )
}

// ─── Equipment Layer ──────────────────────────────────────
function EquipmentLayer({ equipment }: { equipment: any[] }) {
  const critical = equipment.filter(e => e.status === 'critical' || e.status === 'offline')
  if (critical.length === 0) return null

  return (
    <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {critical.slice(0, 5).map(eq => (
        <motion.div
          key={eq.id}
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ repeat: Infinity, duration: 1.4 }}
          style={{
            background:   'rgba(239,68,68,0.15)',
            border:       '1px solid rgba(239,68,68,0.5)',
            borderRadius: 4,
            padding:      '3px 8px',
            fontSize:     10,
            color:        '#ef4444',
          }}
        >
          ⚙️ {eq.nome}
        </motion.div>
      ))}
    </div>
  )
}

// ─── Risk Row ─────────────────────────────────────────────
function RiskRow({ risk }: { risk: SpatialRisk }) {
  const LAYER = LAYERS.find(l => l.riskType === risk.risk_type)
  const color = LAYER?.color ?? '#6b7280'
  const pct   = Math.round(risk.score * 100)

  return (
    <div style={{
      display:      'flex',
      alignItems:   'center',
      gap:          10,
      padding:      '6px 10px',
      background:   SURFACE.card,
      borderRadius: 6,
      border:       `1px solid ${color}22`,
    }}>
      <span style={{ fontSize: 14 }}>{LAYER?.icon ?? '⚠️'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {risk.title}
        </div>
      </div>
      <RiskBadge level={risk.severity} small />
      <div style={{ width: 32, textAlign: 'right', fontSize: 11, fontWeight: 700, color }}>
        {pct}
      </div>
    </div>
  )
}
